/**
 * Orchestrator API client hooks using TanStack Query
 * Provides hooks for session management and orchestration requests
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiOrigin } from '@/lib/config'
import { ApiError } from './client'
import { projectKeys } from './projects'
import type {
  Session,
  SessionsListResponse,
  SessionsFilterParams,
  SessionStatsResponse,
  SessionStatsParams,
  SessionStatus,
  SessionMode,
  SessionMessage
} from '@/lib/types'
import { apiFetch } from './http'

/**
 * Get the orchestrator API base URL from config
 * Ensures trailing slash for correct URL resolution with new URL()
 */
function getOrchestratorUrl(): string {
  // Absolute, because the callers below build URLs with `new URL(path, base)` and a relative base throws.
  // Behind the BFF the configured origin is empty, which means *this* origin — same-origin is the point
  // (checklist D5), and an absolute URL to the page's own origin is still same-origin.
  return `${apiOrigin() || window.location.origin}/`
}

// ============================================================================
// Background Session Types
// ============================================================================

/**
 * Request to start a background orchestration task
 */
export interface StartBackgroundTaskRequest {
  /** The user's task description */
  userRequest: string
  /** Optional session ID (will be generated if not provided) */
  sessionId?: string
  /** Optional project ID to associate the session with */
  projectId?: string
  /** Optional session-level constraints */
  constraints?: string[]
}

/**
 * Response from starting a background task
 */
export interface StartBackgroundTaskResponse {
  /** The session ID for the background task */
  sessionId: string
  /** Current status of the session */
  status: SessionStatus
  /** Execution mode (should be 'Background') */
  mode: SessionMode
  /** Human-readable message */
  message: string
}

/**
 * Response from cancelling a session
 */
export interface CancelSessionResponse {
  /** The session ID that was cancelled */
  sessionId: string
  /** Whether the cancellation was successful */
  success: boolean
  /** Current status after cancellation */
  status: SessionStatus
  /** Human-readable message */
  message: string
}

/**
 * Query key factory for sessions
 */
export const sessionKeys = {
  all: ['sessions'] as const,
  list: (filters: SessionsFilterParams = {}) =>
    ['sessions', 'list', filters] as const,
  detail: (sessionId: string) => ['sessions', 'detail', sessionId] as const,
  stats: (params: SessionStatsParams = {}) =>
    ['sessions', 'stats', params] as const
}

/**
 * Fetch sessions list from orchestrator API
 */
async function fetchSessions(
  params: SessionsFilterParams = {}
): Promise<SessionsListResponse> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL('api/sessions', baseUrl)

  if (params.limit !== undefined) {
    url.searchParams.set('limit', String(params.limit))
  }
  if (params.offset !== undefined) {
    url.searchParams.set('offset', String(params.offset))
  }
  if (params.includeExpired !== undefined) {
    url.searchParams.set('includeExpired', String(params.includeExpired))
  }
  if (params.projectId) {
    url.searchParams.set('projectId', params.projectId)
  }
  if (params.search) {
    url.searchParams.set('search', params.search)
  }
  if (params.status && params.status.length > 0) {
    // Pass status array as multiple query params: ?status=Running&status=Failed
    params.status.forEach(s => url.searchParams.append('status', s))
  }
  if (params.mode) {
    url.searchParams.set('mode', params.mode)
  }
  if (params.excludeMode) {
    url.searchParams.set('excludeMode', params.excludeMode)
  }
  if (params.dateFrom) {
    url.searchParams.set('dateFrom', params.dateFrom.toISOString())
  }
  if (params.dateTo) {
    url.searchParams.set('dateTo', params.dateTo.toISOString())
  }

  const response = await apiFetch(url.toString())

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }

  return response.json()
}

/**
 * Pagination options for session messages
 */
export interface SessionMessagePaginationOptions {
  /** Maximum number of messages to return */
  limit?: number
  /** Number of messages to skip from the end */
  offset?: number
}

/**
 * Fetch a single session by ID with optional pagination for messages
 */
async function fetchSession(sessionId: string, options?: SessionMessagePaginationOptions): Promise<Session> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL(`api/sessions/${sessionId}`, baseUrl)
  
  if (options?.limit !== undefined) {
    url.searchParams.set('limit', options.limit.toString())
  }
  if (options?.offset !== undefined) {
    url.searchParams.set('offset', options.offset.toString())
  }

  const response = await apiFetch(url.toString())

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(`${errorData.error || "HTTP error"}`, response.status, response.statusText, errorData)
  }

  return response.json()
}

/**
 * Delete a session by ID
 */
async function deleteSession(sessionId: string): Promise<void> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL(`api/sessions/${sessionId}`, baseUrl)

  const response = await apiFetch(url.toString(), {
    method: 'DELETE',
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(`${errorData.error || "HTTP error"}`, response.status, response.statusText, errorData)
  }
}

/**
 * Hook to fetch sessions list with automatic polling
 *
 * @param params - Optional filter parameters (limit, offset, includeExpired)
 * @returns Query result with sessions list
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useSessions({ limit: 10 })
 * ```
 */
export function useSessions(params: SessionsFilterParams = {}) {
  return useQuery({
    queryKey: sessionKeys.list(params),
    queryFn: () => fetchSessions(params),
    refetchInterval: 5000, // Poll every 5 seconds to show running sessions
    refetchIntervalInBackground: false // Don't poll when tab is not visible
  })
}

/**
 * Hook to fetch a single session by ID
 *
 * @param sessionId - The session ID to fetch (undefined to disable)
 * @returns Query result with session data
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useSession('session-123')
 * ```
 */
export function useSession(sessionId: string | undefined) {
  return useQuery({
    queryKey: sessionKeys.detail(sessionId!),
    queryFn: () => fetchSession(sessionId!),
    enabled: !!sessionId
  })
}

/**
 * Hook to delete a session
 *
 * @returns Mutation for deleting a session
 *
 * @example
 * ```tsx
 * const deleteSession = useDeleteSession()
 * deleteSession.mutate('session-123')
 * ```
 */
export function useDeleteSession() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteSession,
    onSuccess: () => {
      // Invalidate sessions list to refetch
      queryClient.invalidateQueries({ queryKey: sessionKeys.all })
      // Also invalidate projects since session counts change
      queryClient.invalidateQueries({ queryKey: projectKeys.all })
    }
  })
}

/**
 * Fetch session statistics from orchestrator API
 */
async function fetchSessionStats(
  params: SessionStatsParams = {}
): Promise<SessionStatsResponse> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL('api/sessions/stats', baseUrl)

  if (params.projectId) {
    url.searchParams.set('projectId', params.projectId)
  }

  const response = await apiFetch(url.toString())

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }

  return response.json()
}

/**
 * Hook to fetch session statistics
 *
 * @param params - Optional parameters (projectId to filter by)
 * @returns Query result with session statistics
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useSessionStats({ projectId: 'proj-123' })
 * ```
 */
export function useSessionStats(params: SessionStatsParams = {}) {
  return useQuery({
    queryKey: sessionKeys.stats(params),
    queryFn: () => fetchSessionStats(params),
    refetchInterval: 30000, // Refresh every 30 seconds
    refetchIntervalInBackground: false
  })
}

// ============================================================================
// Background Session API Functions
// ============================================================================

/**
 * Start a background orchestration task.
 * The task will execute asynchronously and the response returns immediately.
 */
async function startBackgroundTask(
  request: StartBackgroundTaskRequest
): Promise<StartBackgroundTaskResponse> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL('api/orchestrate/stream', baseUrl)

  const response = await apiFetch(url.toString(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      userRequest: request.userRequest,
      sessionId: request.sessionId,
      projectId: request.projectId,
      constraints: request.constraints,
      background: true // This triggers background mode
    })
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }

  return response.json()
}

/**
 * Cancel a running or queued session.
 */
async function cancelSession(sessionId: string): Promise<CancelSessionResponse> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL(`api/sessions/${sessionId}/cancel`, baseUrl)

  const response = await apiFetch(url.toString(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: '{}' // Empty JSON object to satisfy FastEndpoints JSON parsing
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }

  return response.json()
}

/**
 * Response from resuming a session
 */
export interface ResumeSessionResponse {
  /** The session ID that was resumed */
  sessionId: string
  /** Current status after resuming */
  status: SessionStatus
  /** Human-readable message */
  message: string
  /** URL to redirect to for session details */
  redirectTo: string
}

/**
 * Resume a paused or failed session as a background task.
 * The session continues asynchronously and user should poll for updates.
 * @param sessionId - The session ID to resume
 */
async function resumeSession(sessionId: string): Promise<ResumeSessionResponse> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL(`api/sessions/${sessionId}/resume`, baseUrl)

  const response = await apiFetch(url.toString(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ userResponse: '' })
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }

  return response.json()
}

// ============================================================================
// Background Session Hooks
// ============================================================================

/**
 * Hook to start a background orchestration task.
 *
 * @returns Mutation for starting a background task
 *
 * @example
 * ```tsx
 * const startTask = useStartBackgroundTask()
 * startTask.mutate({
 *   userRequest: 'Create a new feature',
 *   projectId: 'proj-123'
 * })
 * ```
 */
export function useStartBackgroundTask() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: startBackgroundTask,
    onSuccess: (data) => {
      // Invalidate sessions list to show the new session
      queryClient.invalidateQueries({ queryKey: sessionKeys.all })
      // Invalidate the specific session if it exists
      queryClient.invalidateQueries({ queryKey: sessionKeys.detail(data.sessionId) })
    }
  })
}

/**
 * Hook to cancel a running or queued session.
 *
 * @returns Mutation for cancelling a session
 *
 * @example
 * ```tsx
 * const cancelSession = useCancelSession()
 * cancelSession.mutate('session-123')
 * ```
 */
export function useCancelSession() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: cancelSession,
    onSuccess: (data) => {
      // Invalidate session lists to reflect the status change
      queryClient.invalidateQueries({ queryKey: sessionKeys.all })
      // Invalidate the specific session
      queryClient.invalidateQueries({ queryKey: sessionKeys.detail(data.sessionId) })
    }
  })
}

/**
 * Hook to resume a paused or failed session as a background task.
 * After resumption, the user should navigate to the session details page
 * and poll for updates.
 *
 * @returns Mutation for resuming a session
 *
 * @example
 * ```tsx
 * const resumeSession = useResumeSession()
 * const handleResume = async () => {
 *   const result = await resumeSession.mutateAsync('session-123')
 *   navigate(result.redirectTo)
 * }
 * ```
 */
export function useResumeSession() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: resumeSession,
    onSuccess: (data) => {
      // Invalidate session lists to reflect the status change
      queryClient.invalidateQueries({ queryKey: sessionKeys.all })
      // Invalidate the specific session
      queryClient.invalidateQueries({ queryKey: sessionKeys.detail(data.sessionId) })
    }
  })
}

/**
 * Hook to fetch a session with automatic polling for running/queued sessions.
 * Polls every 5 seconds when session is in an active state.
 * Uses pagination to load only the most recent messages initially.
 *
 * @param sessionId - The session ID to fetch
 * @param options - Pagination options (limit defaults to 20)
 * @returns Query result with session data and automatic polling
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useSessionWithPolling('session-123')
 * ```
 */
export function useSessionWithPolling(sessionId: string | undefined, options?: SessionMessagePaginationOptions) {
  // By default, don't pass pagination params (limit=0 means all messages on backend)
  // Only include pagination in query key if explicitly provided
  const queryKeyOptions = options ? { limit: options.limit, offset: options.offset } : undefined
  
  return useQuery({
    queryKey: queryKeyOptions 
      ? [...sessionKeys.detail(sessionId!), queryKeyOptions] 
      : sessionKeys.detail(sessionId!),
    queryFn: () => fetchSession(sessionId!, options),
    enabled: !!sessionId,
    // Widening the message window changes the key; keep showing the current page meanwhile
    // instead of dropping back to the loading skeleton.
    placeholderData: (previous) => previous,
    refetchInterval: (query) => {
      // Poll every 5 seconds for active sessions
      const session = query.state.data
      const activeStatuses: SessionStatus[] = ['Running', 'Queued', 'WaitingForInput']
      if (session && activeStatuses.includes(session.metadata.status)) {
        return 5000
      }
      return false // Stop polling for completed/failed/cancelled sessions
    },
    refetchIntervalInBackground: false
  })
}

/**
 * Fetch additional (older) messages for a session.
 * Used for "load more" functionality.
 */
export async function fetchSessionMessages(
  sessionId: string, 
  options: SessionMessagePaginationOptions
): Promise<{ messages: SessionMessage[]; totalCount: number; hasMore: boolean }> {
  const session = await fetchSession(sessionId, options)
  return {
    messages: session.messages,
    totalCount: session.totalCount ?? session.messages.length,
    hasMore: session.hasMore ?? false
  }
}

// ============================================================================
// Token Usage API
// ============================================================================

import type { TokenUsage, TokenStats, TokenTimeSeriesResponse, TopSessionsResponse } from '@/types/chatbot'

/**
 * Query key factory for token usage
 */
export const tokenKeys = {
  all: ['tokens'] as const,
  session: (sessionId: string) => ['tokens', 'session', sessionId] as const,
  stats: (from?: string, to?: string) => ['tokens', 'stats', { from, to }] as const,
  timeSeries: (from?: string, to?: string, granularity?: string) => 
    ['tokens', 'timeseries', { from, to, granularity }] as const,
  topSessions: (limit?: number, from?: string, to?: string) => 
    ['tokens', 'top-sessions', { limit, from, to }] as const,
  overallThresholdStatus: () => ['tokens', 'threshold-status', 'overall'] as const,
  sessionThresholdStatus: (sessionId: string) => ['tokens', 'threshold-status', 'session', sessionId] as const,
}

/**
 * Fetch token usage for a specific session
 */
async function fetchSessionTokens(sessionId: string): Promise<TokenUsage> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL(`api/sessions/${sessionId}/tokens`, baseUrl)

  const response = await apiFetch(url.toString())

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `Failed to fetch token usage for session ${sessionId}`,
      response.status,
      response.statusText
    )
  }

  return response.json()
}

/**
 * Parameters for token stats query
 */
export interface TokenStatsParams {
  /** Start date (ISO string) */
  from?: string;
  /** End date (ISO string) */
  to?: string;
}

/**
 * Fetch aggregated token statistics
 */
async function fetchTokenStats(params: TokenStatsParams = {}): Promise<TokenStats> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL('api/stats/tokens', baseUrl)
  
  if (params.from) url.searchParams.set('from', params.from)
  if (params.to) url.searchParams.set('to', params.to)

  const response = await apiFetch(url.toString())

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || 'Failed to fetch token statistics',
      response.status,
      response.statusText
    )
  }

  return response.json()
}

/**
 * Hook to fetch token usage for a specific session
 *
 * @param sessionId - The session ID to fetch token usage for
 * @param options - Optional query options
 * @returns Query result with token usage data
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useSessionTokens('session-123')
 * if (data) {
 *   console.log(`Total tokens: ${data.totalTokens}`)
 * }
 * ```
 */
export function useSessionTokens(
  sessionId: string | undefined,
  options?: { enabled?: boolean; refetchInterval?: number }
) {
  return useQuery({
    queryKey: tokenKeys.session(sessionId!),
    queryFn: () => fetchSessionTokens(sessionId!),
    enabled: !!sessionId && (options?.enabled !== false),
    refetchInterval: options?.refetchInterval,
    staleTime: 30_000, // Consider data fresh for 30 seconds
  })
}

/**
 * Hook to fetch aggregated token statistics
 *
 * @param params - Optional date range parameters
 * @param options - Optional query options
 * @returns Query result with aggregated token stats
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useTokenStats({ from: '2025-01-01', to: '2025-01-31' })
 * if (data) {
 *   console.log(`Sessions tracked: ${data.sessionCount}`)
 * }
 * ```
 */
export function useTokenStats(
  params: TokenStatsParams = {},
  options?: { enabled?: boolean; refetchInterval?: number }
) {
  return useQuery({
    queryKey: tokenKeys.stats(params.from, params.to),
    queryFn: () => fetchTokenStats(params),
    enabled: options?.enabled !== false,
    refetchInterval: options?.refetchInterval,
    staleTime: 60_000, // Consider stats fresh for 1 minute
  })
}

/**
 * Parameters for time-series token data
 */
export interface TokenTimeSeriesParams {
  /** Start date (ISO string) */
  from?: string;
  /** End date (ISO string) */
  to?: string;
  /** Granularity: "day" or "hour" */
  granularity?: 'day' | 'hour';
}

/**
 * Fetch token usage time-series data
 */
async function fetchTokenTimeSeries(params: TokenTimeSeriesParams = {}): Promise<TokenTimeSeriesResponse> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL('api/stats/tokens/timeseries', baseUrl)
  
  if (params.from) url.searchParams.set('from', params.from)
  if (params.to) url.searchParams.set('to', params.to)
  if (params.granularity) url.searchParams.set('granularity', params.granularity)

  const response = await apiFetch(url.toString())

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || 'Failed to fetch token time-series data',
      response.status,
      response.statusText
    )
  }

  return response.json()
}

/**
 * Hook to fetch token usage time-series data
 *
 * @param params - Time range and granularity parameters
 * @param options - Optional query options
 * @returns Query result with time-series data
 *
 * @example
 * ```tsx
 * const { data } = useTokenTimeSeries({ from: '2025-01-01', granularity: 'day' })
 * ```
 */
export function useTokenTimeSeries(
  params: TokenTimeSeriesParams = {},
  options?: { enabled?: boolean; refetchInterval?: number }
) {
  return useQuery({
    queryKey: tokenKeys.timeSeries(params.from, params.to, params.granularity),
    queryFn: () => fetchTokenTimeSeries(params),
    enabled: options?.enabled !== false,
    refetchInterval: options?.refetchInterval,
    staleTime: 60_000, // Consider data fresh for 1 minute
  })
}

/**
 * Parameters for top sessions query
 */
export interface TopSessionsParams {
  /** Maximum number of sessions (default 10) */
  limit?: number;
  /** Start date filter (ISO string) */
  from?: string;
  /** End date filter (ISO string) */
  to?: string;
}

/**
 * Fetch top sessions by token usage
 */
async function fetchTopSessionsByTokens(params: TopSessionsParams = {}): Promise<TopSessionsResponse> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL('api/stats/tokens/top-sessions', baseUrl)
  
  if (params.limit) url.searchParams.set('limit', params.limit.toString())
  if (params.from) url.searchParams.set('from', params.from)
  if (params.to) url.searchParams.set('to', params.to)

  const response = await apiFetch(url.toString())

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || 'Failed to fetch top sessions',
      response.status,
      response.statusText
    )
  }

  return response.json()
}

/**
 * Hook to fetch top sessions by token usage
 *
 * @param params - Query parameters (limit, date range)
 * @param options - Optional query options
 * @returns Query result with top sessions
 *
 * @example
 * ```tsx
 * const { data } = useTopSessionsByTokens({ limit: 10 })
 * ```
 */
export function useTopSessionsByTokens(
  params: TopSessionsParams = {},
  options?: { enabled?: boolean; refetchInterval?: number }
) {
  return useQuery({
    queryKey: tokenKeys.topSessions(params.limit, params.from, params.to),
    queryFn: () => fetchTopSessionsByTokens(params),
    enabled: options?.enabled !== false,
    refetchInterval: options?.refetchInterval,
    staleTime: 60_000, // Consider data fresh for 1 minute
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// Token Threshold Status APIs
// ─────────────────────────────────────────────────────────────────────────────

import type { OverallThresholdStatusResponse, SessionThresholdStatusResponse } from '@/types/pricing'

/**
 * Fetch overall token threshold status across all models
 */
async function fetchOverallThresholdStatus(): Promise<OverallThresholdStatusResponse> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL('api/token-stats/threshold-status', baseUrl)

  const response = await apiFetch(url.toString())

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || 'Failed to fetch overall threshold status',
      response.status,
      response.statusText
    )
  }

  return response.json()
}

/**
 * Hook to fetch overall token threshold status
 *
 * @param options - Optional query options
 * @returns Query result with overall threshold status per model
 *
 * @example
 * ```tsx
 * const { data } = useOverallThresholdStatus()
 * if (data) {
 *   data.models.forEach(model => {
 *     console.log(`${model.modelName}: input=${model.input.status}, output=${model.output.status}`)
 *   })
 * }
 * ```
 */
export function useOverallThresholdStatus(options?: { enabled?: boolean; refetchInterval?: number }) {
  return useQuery({
    queryKey: tokenKeys.overallThresholdStatus(),
    queryFn: fetchOverallThresholdStatus,
    enabled: options?.enabled !== false,
    refetchInterval: options?.refetchInterval,
    staleTime: 60_000, // Consider data fresh for 1 minute
  })
}

/**
 * Fetch session-specific token threshold status
 */
async function fetchSessionThresholdStatus(sessionId: string): Promise<SessionThresholdStatusResponse> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL(`api/sessions/${sessionId}/token-threshold-status`, baseUrl)

  const response = await apiFetch(url.toString())

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `Failed to fetch threshold status for session ${sessionId}`,
      response.status,
      response.statusText
    )
  }

  return response.json()
}

/**
 * Hook to fetch session-specific token threshold status
 *
 * @param sessionId - The session ID to get threshold status for
 * @param options - Optional query options
 * @returns Query result with session threshold status per model
 *
 * @example
 * ```tsx
 * const { data } = useSessionThresholdStatus('session-123')
 * if (data) {
 *   data.models.forEach(model => {
 *     console.log(`${model.modelName}: input=${model.input.status}, output=${model.output.status}`)
 *   })
 * }
 * ```
 */
export function useSessionThresholdStatus(
  sessionId: string | undefined,
  options?: { enabled?: boolean; refetchInterval?: number }
) {
  return useQuery({
    queryKey: tokenKeys.sessionThresholdStatus(sessionId!),
    queryFn: () => fetchSessionThresholdStatus(sessionId!),
    enabled: !!sessionId && options?.enabled !== false,
    refetchInterval: options?.refetchInterval,
    staleTime: 60_000, // Consider data fresh for 1 minute
  })
}
